import { useState } from 'react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { Search, CheckCircle2, Eye, Package, Truck } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { StatusBadge } from '@/components/shared/status-badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useWarehouseOrders, useFulfillWarehouseOrder } from '@/hooks/use-warehouses';
import { formatDateTime } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { WAREHOUSE_STATUS_LABELS } from './warehouse-status';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const poViewHref = (id: string) => `#/pos-management/purchase-orders/${id}/edit`;

export function WarehouseOrdersListPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const fulfillOrder = useFulfillWarehouseOrder();
  const canFulfil = usePermissions().has('sales_orders.fulfil');
  const [fulfillTarget, setFulfillTarget] = useState<{ id: string; orderNumber: number } | null>(null);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const { data, isLoading } = useWarehouseOrders({
    page,
    perPage,
    status: statusFilter || undefined,
  });

  const orders = data?.data ?? [];
  const pagination = data?.pagination;

  const handleFulfill = (id: string, orderNumber: number) => {
    setFulfillTarget({ id, orderNumber });
  };

  const confirmFulfill = async () => {
    if (!fulfillTarget) return;
    try {
      await fulfillOrder.mutateAsync(fulfillTarget.id);
      setFulfillTarget(null);
    } catch {
      // Error handled by global handler
      setFulfillTarget(null);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'orderNumber',
      header: 'Order #',
      size: 70,
      // Opens the PO edit route, which renders the restricted read-only owner
      // view for orders placed by another tenant.
      cell: ({ row }) => (
        <a href={poViewHref(row.original.id)} className="font-medium text-primary hover:underline">
          #{row.original.orderNumber}
        </a>
      ),
    },
    {
      accessorKey: 'orderingCompany',
      header: 'Ordering Company',
    },
    {
      accessorKey: 'locationName',
      header: 'Location',
    },
    {
      accessorKey: 'createdByName',
      header: 'Ordered By',
      cell: ({ row }) => row.original.createdByName || 'System',
    },
    {
      accessorKey: 'purpose',
      header: 'Purpose',
      cell: ({ row }) =>
        row.original.purpose ? (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-blue-700">
            {row.original.purpose}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      // Warehouse-side terminal state is 'fulfilled' (we shipped it). Once the
      // buyer marks the PO 'received' the canonical DB status advances; it's
      // labelled "Received by buyer" (same as the status filter) so the
      // warehouse user doesn't see a bare "Received" on a row their tenant
      // didn't receive anything for.
      cell: ({ row }) => <StatusBadge status={row.original.status} labelMap={WAREHOUSE_STATUS_LABELS} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 160,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <a
            href={poViewHref(row.original.id)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
            title="View"
          >
            <Eye className="h-4 w-4 text-muted-foreground" />
          </a>
          {row.original.status === 'sent' && canFulfil && (
            <button
              onClick={() => handleFulfill(row.original.id, row.original.orderNumber)}
              disabled={fulfillOrder.isPending}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
              title="Mark as Fulfilled"
            >
              <Package className="h-3.5 w-3.5" />
              Fulfill
            </button>
          )}
          {row.original.status === 'fulfilled' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700">
              <CheckCircle2 className="h-3 w-3" /> Fulfilled
            </span>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({ data: orders, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="space-y-4">
      <PageHeader title="Warehouse Orders" />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="sent">In Progress</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="received">Received by buyer</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="No incoming orders from companies yet"
          description="Orders will appear here when companies order from this warehouse."
          className="h-64"
        />
      ) : (
        <>
          <DataTable table={table} columns={columns} />
          {pagination && (
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={handlePerPageChange}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={!!fulfillTarget}
        title="Mark as Fulfilled?"
        message={fulfillTarget ? `Mark Order #${fulfillTarget.orderNumber} as fulfilled?` : ''}
        confirmLabel="Mark Fulfilled"
        variant="info"
        loading={fulfillOrder.isPending}
        onConfirm={confirmFulfill}
        onCancel={() => setFulfillTarget(null)}
      />
    </div>
  );
}
